(function () {
    // save the section id from the script tag and find the section
    const sectionID = document.currentScript.dataset.id;
    const section = document.querySelector(`#${sectionID}`);

    // exit if section is not found
    if (!section) {
        console.warn(`Product section with ID ${sectionID} not found.`);
        return;
    }

    // get product data
    const product = JSON.parse(section.querySelector(".productV1JSON").textContent);
    // starting activeVariant comes from liquid; replaced by server fetch when options change
    let activeVariant = JSON.parse(section.querySelector(".productV1VariantJSON").textContent);

    // during dev mode, set the active variant to the window so it can be accessed in the console
    if (Shopify.theme.role === "development") {
        window.product = product;
        window.activeVariant = activeVariant;
    }

    /************
     Version picker (MagSafe vs Adhesive)
     snippets/pdp-version-picker.liquid renders two cards above the swatches and embeds the
     ALTERNATE product's JSON. While "Adhesive" is picked, every line that would have been one
     of this product's variants is swapped for the alternate product's variant in the same
     Color value - the plain add, the PDP card in a bundle, and a bundle's second grip if that
     color exists in adhesive. Adhesive can be picked only for colors the alternate product comes
     in. On other colors the adhesive card is locked (disabled, with its lock line) when the
     snippet rendered one, or the whole picker hides when it didn't. Locked or hidden, the choice
     always reads as MagSafe.
     ************/
    const versionPicker = section.querySelector("[data-version-picker]");
    const altCard = versionPicker?.querySelector("[data-version-alt-card]") || null;
    const altInput = altCard?.querySelector(".pdpVersionInput") || null;
    // the lock line exists only when "Adhesive Unavailable Text" is set
    const lockedNote = altCard?.querySelector(".pdpVersionCardLockedNote") || null;
    let altProduct = null;
    if (versionPicker) {
        try {
            altProduct = JSON.parse(versionPicker.querySelector(".pdpVersionAltJSON").textContent);
        } catch (err) {
            console.error("Version picker: alternate product JSON failed to parse:", err);
        }
    }

    // index of the Color option; product JSON lists option names as plain strings
    function colorIndexOf(p) {
        const idx = (p?.options || []).findIndex(o => String(o?.name || o).toLowerCase() === "color");
        return idx === -1 ? 0 : idx;
    }
    const productColorIndex = colorIndexOf(product);
    const altColorIndex = altProduct ? colorIndexOf(altProduct) : 0;

    // colors the picker may appear on (section setting, comma-separated); empty = any color the alternate product comes in
    const versionColors = (versionPicker?.dataset.colors || "")
        .split(",").map(c => c.trim().toLowerCase()).filter(Boolean);

    // the alternate product's purchasable variant in a color, or null when the color is not allowed or it does not come in it
    function altVariantFor(colorValue) {
        if (!altProduct || !colorValue) return null;
        if (versionColors.length && !versionColors.includes(String(colorValue).trim().toLowerCase())) return null;
        return altProduct.variants.find(v => v.available && v.options[altColorIndex] === colorValue) || null;
    }

    // some color offers the adhesive version: the lock line names one, so it shows only while one exists
    const anyAltOffered = !!altProduct && altProduct.variants.some(v => !!altVariantFor(v.options[altColorIndex]));

    function activeColor() {
        return activeVariant?.options?.[productColorIndex] || null;
    }

    // on the page AND on screen: not hidden for this color (display check also covers a hidden product-tab group)
    function versionPickerVisible() {
        if (!versionPicker || versionPicker.hidden) return false;
        return window.getComputedStyle(versionPicker).display !== "none";
    }

    // on screen with Adhesive pickable (not locked for this color)
    function versionChoiceOpen() {
        return versionPickerVisible() && !!altInput && !altInput.disabled;
    }

    function versionChoice() {
        if (!versionChoiceOpen()) return "magsafe";
        const checked = versionPicker.querySelector(".pdpVersionInput:checked");
        return checked && checked.value === "adhesive" ? "adhesive" : "magsafe";
    }

    // one of THIS product's variants -> its adhesive counterpart while that is the choice; anything else passes through
    function resolveVariant(variant) {
        if (!variant || versionChoice() !== "adhesive") return variant;
        if (!product.variants.some(v => v.id === variant.id)) return variant;
        return altVariantFor(variant.options[productColorIndex]) || variant;
    }

    function resolveVariantId(variantId) {
        const variant = product.variants.find(v => v.id === variantId);
        return variant ? resolveVariant(variant).id : variantId;
    }

    // pricing for a line that may have been swapped: the alternate variant's own price and compare-at
    function linePricing(variantId, price, compare) {
        const resolvedId = resolveVariantId(variantId);
        if (resolvedId !== variantId && altProduct) {
            const alt = altProduct.variants.find(v => v.id === resolvedId);
            if (alt) return {price: alt.price, compare: alt.compare_at_price || 0};
        }
        return {price: price, compare: compare};
    }

    // line item property stamped on every grip line while the choice was open. Named for the
    // line's own version, so a Black+Orchid 2-pack with Adhesive picked reads adhesive + magsafe.
    // Nothing is stamped when the picker was hidden or locked: "never offered" stays its own bucket.
    function versionPropertiesFor(variantId) {
        if (!versionChoiceOpen()) return null;
        if (altProduct && altProduct.variants.some(v => v.id === variantId)) return {_grip_version: "adhesive"};
        if (product.variants.some(v => v.id === variantId)) return {_grip_version: "magsafe"};
        return null;
    }

    // GTM dataLayer events for take-rate reporting (offered / selected / added)
    function trackVersion(event, extra) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(Object.assign({
            event: event,
            grip_version: versionChoice(),
            grip_color: activeColor(),
            grip_product: product.handle,
        }, extra || {}));
    }

    // Personalize has no adhesive version (its mirror products are MagSafe only), so the
    // Personalize button hides while Adhesive is picked (main-product-v1.css). Personalize mode
    // itself hides this buy box, picker included, so the choice can't change once inside it.
    function syncVersionPersonalize() {
        section.classList.toggle("pdpVersionAdhesive", versionChoice() === "adhesive");
    }

    const VERSION_PARAM = "version";
    function syncVersionUrl() {
        const url = new URL(window.location.href);
        if (versionChoice() === "adhesive") {
            url.searchParams.set(VERSION_PARAM, "adhesive");
        } else {
            url.searchParams.delete(VERSION_PARAM);
        }
        window.history.replaceState({}, "", url);
    }

    // ?version=adhesive preselects the adhesive card (shared links, QA); it only counts once the
    // picker is actually visible for the selected color, which versionChoice() enforces
    if (versionPicker) {
        try {
            if (new URLSearchParams(window.location.search).get(VERSION_PARAM) === "adhesive") {
                const altInput = versionPicker.querySelector('.pdpVersionInput[value="adhesive"]');
                if (altInput) altInput.checked = true;
            }
        } catch (e) {}
    }

    // a color the alternate product comes in unlocks the adhesive card; any other color locks it
    // (or hides the picker, without a lock line). Locking also resets the choice so a stale
    // "Adhesive" can never leak into a color that has no adhesive variant
    let versionOffered = false;
    function syncVersionPicker() {
        if (!versionPicker) return;
        const alt = altVariantFor(activeColor());
        const locked = !alt;
        versionPicker.hidden = locked && !(lockedNote && anyAltOffered);
        if (altInput) altInput.disabled = locked;
        altCard?.classList.toggle("isLocked", locked);
        if (locked) {
            const defaultInput = versionPicker.querySelector('.pdpVersionInput[value="magsafe"]');
            if (defaultInput) defaultInput.checked = true;
        }
        // gripversion (Shoplift test): tell the gate whether the picker shows for this color (locked
        // included). It enrols once such a color is selected AND the shopper has scrolled to the picker
        window.ohsnapGripVersionOffered?.(!versionPicker.hidden);
        // "offered" keeps its meaning for take-rate: the first time Adhesive could actually be picked
        if (versionChoiceOpen() && !versionOffered) {
            versionOffered = true;
            trackVersion("grip_version_offered");
        }
        syncVersionUrl();
        syncVersionPersonalize();
    }

    // gripversion: the gate decides the arm asynchronously after a color pick, so re-sync once it
    // has (fires the "offered" event and the swatch/URL state for a variant shopper)
    document.addEventListener("ohsnap:grip-version-decided", () => {
        syncVersionPicker();
        renderPrice();
    });

    // the generic input listener below already re-runs updateProduct (and so the price) on change
    versionPicker?.querySelectorAll(".pdpVersionInput").forEach(input => {
        input.addEventListener("change", () => {
            syncVersionUrl();
            syncVersionPersonalize();
            trackVersion("grip_version_select");
        });
    });

    // info popover on the adhesive card: a button inside the label, so stop the click reaching the radio
    section.addEventListener("click", (e) => {
        const infoBtn = e.target.closest(".pdpVersionInfo");
        if (!infoBtn) return;
        e.preventDefault();
        const popover = infoBtn.parentElement.querySelector(".pdpVersionInfoPopover");
        if (!popover) return;
        const willOpen = !popover.classList.contains("active");
        popover.classList.toggle("active", willOpen);
        infoBtn.setAttribute("aria-expanded", willOpen ? "true" : "false");
    });

    document.addEventListener("mousedown", (e) => {
        if (e.target.closest(".pdpVersionInfo, .pdpVersionInfoPopover")) return;
        section.querySelectorAll(".pdpVersionInfoPopover.active").forEach(p => {
            p.classList.remove("active");
            p.parentElement.querySelector(".pdpVersionInfo")?.setAttribute("aria-expanded", "false");
        });
    });

    /************
     Product Swiper
     ************/

        // one swiper per variant, stacked; only the active variant's swiper is visible.
        // building a Swiper per variant up front blocks the main thread on large products, so init lazily
    const swiperMap = new Map();

    // build (or return the cached) swiper for a variant's visual block
    function initSwiper(visual) {
        const variantId = visual.dataset.id;
        if (swiperMap.has(variantId)) {
            return swiperMap.get(variantId);
        }

        const swiperEl = visual.querySelector(".productV1VisualSwiper");
        if (!swiperEl) return null;

        // thumbnail strip is its own swiper; slidesPerGroup is 1 so we can nudge it
        // by single slides (see keepThumbInView). the arrows still page a full view
        const thumbsEl = visual.querySelector(".productV1VisualThumbs");
        const thumbsSwiper = new Swiper(thumbsEl, {
            slidesPerView: 4,
            slidesPerGroup: 1,
            spaceBetween: 10,
            watchOverflow: true,
            // slides are buttons w/ their own aria labels
            a11y: false,
            breakpoints: {
                801: {slidesPerView: 5},
            },
        });

        // page the thumbs a full view per arrow click, and mirror the lock/disabled
        // visibility the navigation module would otherwise manage for the arrows
        const thumbsPrev = thumbsEl.querySelector(".productV1VisualThumbsNavPrev");
        const thumbsNext = thumbsEl.querySelector(".productV1VisualThumbsNavNext");

        function updateThumbsNav() {
            thumbsPrev.classList.toggle("swiper-button-lock", thumbsSwiper.isLocked);
            thumbsNext.classList.toggle("swiper-button-lock", thumbsSwiper.isLocked);
            thumbsPrev.classList.toggle("swiper-button-disabled", thumbsSwiper.isBeginning);
            thumbsNext.classList.toggle("swiper-button-disabled", thumbsSwiper.isEnd);
        }

        thumbsPrev.addEventListener("click", () => {
            thumbsSwiper.slideTo(thumbsSwiper.activeIndex - Math.ceil(thumbsSwiper.params.slidesPerView));
        });
        thumbsNext.addEventListener("click", () => {
            thumbsSwiper.slideTo(thumbsSwiper.activeIndex + Math.ceil(thumbsSwiper.params.slidesPerView));
        });
        thumbsSwiper.on("slideChange", updateThumbsNav);
        thumbsSwiper.on("update", updateThumbsNav);
        thumbsSwiper.on("resize", updateThumbsNav);
        updateThumbsNav();

        // nudge the strip by the minimum amount so the active thumb keeps 2
        // thumbnails after it (or 2 before it when moving back), clamped at the ends
        function keepThumbInView(index) {
            const perView = Math.ceil(thumbsSwiper.params.slidesPerView);
            const maxFirst = Math.max(0, thumbsSwiper.slides.length - perView);
            const first = thumbsSwiper.activeIndex;
            const last = first + perView - 1;
            let target = first;
            if (index > last - 2) {
                target = index - perView + 3;
            } else if (index < first + 2) {
                target = index - 2;
            }
            target = Math.max(0, Math.min(target, maxFirst));
            if (target !== first) {
                thumbsSwiper.slideTo(target);
            }
        }

        // mirror the active slide onto the thumb strip and keep it framed
        function syncThumb(index) {
            visual.querySelector(".productV1VisualThumbsSlide.active")?.classList.remove("active");
            visual.querySelectorAll(".productV1VisualThumbsSlide")[index]?.classList.add("active");
            keepThumbInView(index);
        }

        const swiper = new Swiper(swiperEl, {
            slidesPerView: 1,
            spaceBetween: 0,
            loop: true,
            autoHeight: true,
            // the dots only show position: the thumbnail strip (visible at every width) is the
            // tap and keyboard control, so the dots stay 7px and need no 24px target (WCAG 2.5.8)
            pagination: {
                el: swiperEl.querySelector(".productV1VisualSwiperPagination"),
                clickable: false,
            },
            on: {
                // keep the thumb strip in sync as the main slide changes
                slideChange: function () {
                    syncThumb(this.realIndex);
                },
            },
        });

        // clicking a thumb jumps the main swiper to that slide
        visual.querySelectorAll(".productV1VisualThumbsSlide").forEach(thumb => {
            thumb.addEventListener("click", function () {
                swiper.slideToLoop(parseInt(this.dataset.index));
            });
        });

        // set the initial active thumb and framing
        syncThumb(swiper.realIndex);

        swiperMap.set(variantId, swiper);

        return swiper;
    }

    // the active variant's own block, or the shared fallback block when it has no gallery of its own
    function visualFor(variantId) {
        return section.querySelector(`.productV1VisualVariant[data-id="${variantId}"]`)
            || section.querySelector('.productV1VisualVariant[data-id="fallback"]');
    }

    // init only the active variant on load; the rest build on first activation
    const activeVisual = visualFor(activeVariant.id);
    if (activeVisual) {
        initSwiper(activeVisual);
    }

    function updateVisual() {
        // herovideo (PDP test): the video shows only while the product's default colour is selected.
        // A page opened on another colour asks Shoplift the first time the default colour is picked,
        // which is when the video could first be seen (see the gate in layout/theme.liquid)
        if (section.dataset.heroVideoHome) {
            const heroVideoAway = String(activeVariant.id) !== section.dataset.heroVideoHome;
            section.toggleAttribute("data-hero-video-away", heroVideoAway);
            if (!heroVideoAway) {
                window.ohsnapHeroVideoPdpEnrol?.();
            }
        }

        const activeVisual = visualFor(activeVariant.id);
        section.querySelectorAll(".productV1VisualVariant").forEach(visual => {
            const isActive = visual === activeVisual;
            visual.classList.toggle("active", isActive);
            if (isActive) {
                // build the swiper on first activation, then re-measure now that it's visible
                initSwiper(visual)?.update();
            }
        });
    }

    // update active variant on input changes
    section.querySelectorAll("input, select").forEach(function (element) {
        element.addEventListener("change", function () {
            updateProduct().catch(err => console.error("updateProduct failed:", err));
        });
    })

    /**************
     Upsell URL Param
     ***************/
    const UPSELL_PARAM = "option";
    
    // used below to delete param from url if it's the default selection
    const defaultUpsellInput = section.querySelector(".pdpUpsellHeaderInput");

    // reflect the selected upsell back into the url
    function syncUpsellUrl() {
        const selected = section.querySelector(".pdpUpsellHeaderInput:checked");
        const url = new URL(window.location.href);
        // if default selection delete the param
        if (!selected || selected === defaultUpsellInput) {
            url.searchParams.delete(UPSELL_PARAM);
        } else {
            url.searchParams.set(UPSELL_PARAM, selected.dataset.handle);
        }
        window.history.replaceState({}, "", url);
    }

    // keep the url in sync whenever the customer switches upsell
    section.querySelectorAll(".pdpUpsellHeaderInput").forEach(input => {
        input.addEventListener("change", syncUpsellUrl);
    });

    /**************
     Render Price
     ***************/

        // free shipping threshold in cents, emitted by the upsells container (0 = disabled)
    const upsells = section.querySelector(".pdpUpsells");
    const freeShippingThreshold = parseInt(upsells?.dataset.freeShippingThreshold) || 0;

    // the variant a bundle card adds to the cart: the PDP card follows the active variant, a
    // color picker its chosen variant, anything else the card's base variant. Shared by the price
    // and addToCart, because the price depends on which cards end up on the same cart line.
    // While Adhesive is picked, a grip variant resolves to its adhesive counterpart here, so the
    // price and the cart line can never disagree about which product a card adds.
    function upsellCardVariant(card) {
        const picker = card.querySelector(".pdpUpsellVariantPicker");
        if (picker && picker.dataset.pdp === "true") {
            return resolveVariantId(activeVariant.id);
        }
        if (picker && !Number.isNaN(parseInt(picker.dataset.variant))) {
            return resolveVariantId(parseInt(picker.dataset.variant));
        }
        return resolveVariantId(parseInt(card.dataset.variant));
    }

    // total one upsell's cards: the PDP card tracks the live active variant, a card with a
    // color picker uses its chosen variant, and a picker-less card (no color options, e.g.
    // a kit) uses its own base price so it still counts toward the total. Returns the raw sums
    // before any bundle discount, the cards themselves (variant, unit price and the card element,
    // whose own data-dp-eligible says whether a scoped code covers it; payableTotal prices them
    // line by line), plus the upsell's own discount percentage.
    function computeUpsellTotal(upsell) {
        let lines = [];
        if (upsell) {
            upsell.querySelectorAll(".pdpUpsellItem").forEach(card => {
                const picker = card.querySelector(".pdpUpsellVariantPicker");
                const variant = upsellCardVariant(card);

                // the PDP-product card always follows the active variant (swapped for adhesive when picked)
                if (picker && picker.dataset.pdp === "true") {
                    const pdpVariant = resolveVariant(activeVariant);
                    lines.push({variant, node: card, price: pdpVariant.price, compare: pdpVariant.compare_at_price || 0});
                    return;
                }

                // default to the card's base variant, then let a color picker override it
                let lineVariantId = parseInt(card.dataset.variant);
                let linePrice = parseInt(card.dataset.price) || 0;
                let lineCompare = parseInt(card.dataset.compare) || 0;
                if (picker && !Number.isNaN(parseInt(picker.dataset.price))) {
                    lineVariantId = parseInt(picker.dataset.variant);
                    linePrice = parseInt(picker.dataset.price);
                    lineCompare = parseInt(picker.dataset.compare) || 0;
                }
                // a second grip in a bundle swaps to adhesive too, when that color exists in adhesive
                const pricing = linePricing(lineVariantId, linePrice, lineCompare);
                lines.push({variant, node: card, price: pricing.price, compare: pricing.compare});
            });
        }

        // no bundle cards (product_only, kit_link, or nothing selected): price the active variant (swapped for adhesive when picked)
        if (lines.length === 0) {
            const soloVariant = resolveVariant(activeVariant);
            lines.push({variant: soloVariant.id, price: soloVariant.price, compare: soloVariant.compare_at_price || 0});
        }

        // sum the line prices; keep each line's own compare-at so real product sales still strike through
        let realPrice = 0;
        let comparePrice = 0;
        lines.forEach(line => {
            realPrice += line.price;
            comparePrice += Math.max(line.compare, line.price);
        });

        const discount = parseInt(upsell?.querySelector(".pdpUpsellHeaderInput")?.dataset.discount, 10) || 0;
        return {realPrice, comparePrice, discount, lines};
    }

    // What the customer actually pays for an upsell: the bundle's own discount AND any discount
    // code on the session, combined under Shopify's own stacking rules and priced line by line,
    // the way the cart will price it (bundleFor in assets/theme-discount.js). Pricing the total
    // instead read 1-4 cents under the cart. window.OhSnapDiscount is published synchronously
    // from <head> (snippets/discount-rules.liquid), so this is safe on the very first render and
    // needs no readiness check; the fallbacks cover an older copy of that snippet and the feature
    // being stripped from the theme entirely, not a load-order race.
    function payableTotal(total) {
        if (window.OhSnapDiscount?.bundleFor) {
            return window.OhSnapDiscount.bundleFor(total.lines, total.discount, section);
        }
        if (window.OhSnapDiscount) {
            return window.OhSnapDiscount.bestFor(total.realPrice, total.discount, section);
        }
        return Math.round(total.realPrice * (1 - total.discount / 100));
    }

    // price the selected upsell card, then flag the free shipping chip on every upsell that qualifies
    function renderPrice() {
        const compareEl = section.querySelector(".productV1ContentSellingPriceCompare");
        const realEl = section.querySelector(".productV1ContentSellingPriceReal");
        const selected = section.querySelector(".pdpUpsellHeaderInput:checked");

        const total = computeUpsellTotal(selected?.closest(".pdpUpsell"));
        const comparePrice = total.comparePrice;
        let realPrice = total.realPrice;

        // apply the bundle's own discount (from the upsell metaobject, via data-discount) so the headline
        // price matches the "% off" badge; the same percentage routes to the Discount Kit bucket at checkout.
        // payableTotal also folds in a discount code on the session, so the headline price is what the
        // customer will actually be charged rather than a list price the checkout then contradicts.
        realPrice = payableTotal(total);

        // show the compare price only when it beats the real price, otherwise hide it. The amount
        // goes beside the visually hidden "Regular price" label, not over it.
        if (compareEl) {
            const compareText = comparePrice > realPrice ? price(comparePrice) : "";
            (compareEl.querySelector("[data-compare-amount]") || compareEl).textContent = compareText;
            compareEl.hidden = !compareText;
        }
        if (realEl) {
            realEl.textContent = price(realPrice);
        }

        refreshFreeShipping();
        refreshCompareSavings();
    }

    // hidden A/B-test badge on every upsell: its discounted total's savings measured against
    // the full compare-at total (display:none in CSS until a test unhides it)
    function refreshCompareSavings() {
        section.querySelectorAll(".pdpUpsell").forEach(upsell => {
            const badge = upsell.querySelector(".pdpUpsellHeaderBadgesCompareSavings");
            if (!badge) {
                return;
            }

            const total = computeUpsellTotal(upsell);
            const comparePrice = total.comparePrice;
            const discountedTotal = payableTotal(total);
            // nearest whole percent, then rounded UP to the next multiple of 5 (38% -> 40%)
            const rawSavings = comparePrice > 0 ? Math.round((comparePrice - discountedTotal) / comparePrice * 100) : 0;
            const savings = Math.ceil(rawSavings / 5) * 5;
            badge.textContent = savings > 0 ? savings + "% Off" : "";
        });
    }

    // each upsell shows its own free shipping chip when its discounted total clears the threshold;
    // the bundle discount is applied at the cart, so compare on what the customer actually pays
    function refreshFreeShipping() {
        section.querySelectorAll(".pdpUpsell").forEach(upsell => {
            const chip = upsell.querySelector(".pdpUpsellHeaderBadgesShipping");
            if (!chip) {
                return;
            }

            // disabled threshold never qualifies
            if (freeShippingThreshold <= 0) {
                chip.classList.remove("active");
                return;
            }

            const discountedTotal = payableTotal(computeUpsellTotal(upsell));
            chip.classList.toggle("active", discountedTotal >= freeShippingThreshold);
        });
    }

    /**************
     Update Product
     ***************/

    async function updateProduct() {

        // find active variant
        activeVariant = await getActiveVariant();

        // check if variant wasn't found (null would be returned from the fetch)
        // the ATC is absent on a personalize mirror product, whose buy box main-product-v1.liquid
        // replaces with a notice - every lookup of it below has to tolerate that
        const atc = section.querySelector(".productV1ContentATC");
        const atcText = section.querySelector(".productV1ContentATCText");

        if (!activeVariant) {
            if (atc) atc.disabled = true;
            if (atcText) atcText.textContent = "Not Available";
            return;
        }

        /**************
         Variant Image
         ***************/

        updateVisual();

        // keep the active PDP product's upsell card(s) in sync with the selected variant
        syncPdpPickers();

        // show/hide the MagSafe vs Adhesive picker for this color before anything is priced
        syncVersionPicker();

        /**************
         Price
         ***************/

        renderPrice();

        /**************
         ATC Enable/Disable
         ***************/

        // per-variant extras (button label override + variant-only info badges) are pre-rendered by
        // liquid into a <template> per variant that has any; most variants have none
        const extras = section.querySelector(`.productV1VariantExtras[data-variant-id="${activeVariant.id}"]`);

        // check if the variant is in stock; a purchasable variant may relabel the button (e.g. "Pre-Order"),
        // sold out always wins over the override
        if (!atc || !atcText) {
            // mirror product: no buy box to update
        } else if (activeVariant.available) {
            atc.disabled = false;
            atcText.textContent = extras?.dataset.atcText || atc.dataset.addText || "Add To Cart";
        } else {
            atc.disabled = true;
            atcText.textContent = atc.dataset.soldOutText || "Sold Out";
        }

        /**************
         Variant Info Badges
         ***************/

        renderVariantBadges(extras);

        /*************
         Update Sticky
         *********/

            // update url with variant id so the selected options are maintained on refresh
        let url = new URL(window.location.href);
        url.searchParams.set("variant", activeVariant.id);
        window.history.replaceState({}, "", url);
    }

    /**
     * Swaps the variant-level info badges to the active variant's set. Product-level badges are static
     * siblings in the same flex wrapper; the slot sits before them. The popover toggles are delegated on
     * the section, so cloned badges need no wiring.
     * @param {HTMLTemplateElement|null} extras - the active variant's pre-rendered extras, if any
     */
    function renderVariantBadges(extras) {
        const slot = section.querySelector(".productV1ContentInfoBadgesVariant");
        if (!slot) return;
        slot.replaceChildren();
        if (extras) slot.append(extras.content.cloneNode(true));
    }

    // on load, update the product
    // this accomplishes loading the correct variant image and price on page load
    updateProduct();

    // The cart reconcile lands after first paint, so a session whose discount was not yet known
    // (or was optimistic and turned out wrong) re-prices here rather than showing a stale figure.
    document.addEventListener("discount:changed", renderPrice);


    /**
     * Fetches the active variant based on the selected options.
     * If there are more than 250 variants, it makes a request to the server to fetch the active variant since Shopify's storefront API has a limit of 250 variants per product.
     * @returns {Promise<Object>} The active variant object
     */
    async function getActiveVariant() {
        // Fetch variant when exceeding Shopify's 250 variant limit
        if (product.variants.length >= 250) {
            const optionIDs = [];
            section.querySelectorAll(".productV1ContentOptionsItemValuesLabelInput:checked, .productV1ContentOptionsItemDropdownPopoverValueInput:checked").forEach(element => {
                optionIDs.push(element.dataset.id);
            });
            const response = await fetch(`${window.location.pathname}?view=active-variant&option_values=${optionIDs.join(',')}`)
                .then(response => response.json());
            return response.active_variant;
        }

        // If there's only one variant, return it immediately
        if (product.variants.length === 1) {
            return product.variants[0];
        }

        // Gather selected values
        const options = [];
        section.querySelectorAll('.productV1ContentOptionsItem').forEach(group => {
            options.push(group.querySelector('input:checked').value);
        });

        // Find matching variant and return it
        return product.variants.find(variant =>
            JSON.stringify(variant.options) === JSON.stringify(options)
        );
    }

    /**************
     Product ATC
     ***************/

    async function addToCart() {
        // the selected upsell card decides what gets added
        const selected = section.querySelector(".pdpUpsellHeaderInput:checked");

        // kit_link cards navigate to the bundle builder instead of adding to cart
        if (selected && selected.dataset.type === "kit_link") {
            const link = selected.closest(".pdpUpsell").querySelector(".pdpUpsellBodyKitLinkButton");
            if (link) {
                window.location.href = link.href;
            }
            return;
        }

        // add loading animation
        section.querySelector(".productV1ContentATC").classList.add("loading");

        // tag bundle lines with the bundle's discount percentage so the matching Discount Kit bucket applies it; a normal add carries no tag and stays full price
        // _bundle_id groups every line of this one bundle so removing a line later can un-bundle only its own siblings (see updateCartItem in theme-mini-cart.js)
        let bundleProperties = null;
        if (selected) {
            const bundleDiscount = parseInt(selected.dataset.discount, 10);
            if (bundleDiscount > 0) {
                const bundleID = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
                bundleProperties = {_bundle: String(bundleDiscount), _bundle_id: bundleID};
            }
        }

        // build the line items: one per bundle card, mirroring renderPrice's per-card logic so a
        // picker-less kit card is still added; fall back to the active variant when there are no cards
        let items = [];
        if (selected) {
            selected.closest(".pdpUpsell").querySelectorAll(".pdpUpsellItem").forEach(card => {
                // the same variant the price was worked out for (upsellCardVariant, adhesive swap included)
                const variantID = upsellCardVariant(card);
                const item = {quantity: 1, id: variantID};
                const properties = Object.assign({}, bundleProperties || {}, versionPropertiesFor(variantID) || {});
                if (Object.keys(properties).length) {
                    item.properties = properties;
                }
                items.push(item);
            });
        }
        if (items.length === 0) {
            const soloID = resolveVariantId(activeVariant.id);
            const item = {quantity: 1, id: soloID};
            const properties = Object.assign({}, bundleProperties || {}, versionPropertiesFor(soloID) || {});
            if (Object.keys(properties).length) {
                item.properties = properties;
            }
            items.push(item);
        }

        // take-rate event: fires only while the choice was actually open
        if (versionChoiceOpen()) {
            trackVersion("grip_version_add_to_cart", {grip_variant_ids: items.map(i => i.id)});
        }

        // add product
        await addProduct({items: items});

        // refresh the in-memory cart behind the button spinner so the drawer opens fully populated
        await refreshCart();

        // remove loading animation
        section.querySelector(".productV1ContentATC").classList.remove("loading");

        // open cart
        await openCart();
    }

    // absent on a personalize mirror product (see updateProduct); throwing here would stop every
    // listener below it from being wired, swatches included
    section.querySelector('.productV1ContentATC')?.addEventListener("click", addToCart);

    /**************
     Product Options
     ***************/

    // update displayed color name when swatch selection changes
    section.querySelectorAll(".productV1ContentOptionsItemValuesLabelInput").forEach(function (input) {
        input.addEventListener("change", function () {
            const item = this.closest(".productV1ContentOptionsItem");
            const selectedLabel = item.querySelector(".productV1ContentOptionsItemLabelSelected");
            if (selectedLabel) {
                selectedLabel.textContent = this.value;
            }
        });
    });

    // toggle a non-color option dropdown; opening one closes any other open dropdown
    section.addEventListener("click", (e) => {
        const toggle = e.target.closest(".productV1ContentOptionsItemDropdownToggle");
        if (!toggle) return;
        const popover = toggle.closest(".productV1ContentOptionsItemDropdown").querySelector(".productV1ContentOptionsItemDropdownPopover");
        const willOpen = !popover.classList.contains("active");
        closeOptionDropdowns();
        if (willOpen) {
            popover.classList.add("active");
            toggle.setAttribute("aria-expanded", "true");
        }
    });

    // selecting a value updates the toggle text and closes the dropdown
    section.querySelectorAll(".productV1ContentOptionsItemDropdownPopoverValueInput").forEach(input => {
        input.addEventListener("change", function () {
            const item = this.closest(".productV1ContentOptionsItemDropdown");
            item.querySelector(".productV1ContentOptionsItemDropdownToggleSelected").textContent = this.value;
            item.querySelector(".productV1ContentOptionsItemDropdownPopover").classList.remove("active");
            item.querySelector(".productV1ContentOptionsItemDropdownToggle").setAttribute("aria-expanded", "false");
        });
    });

    // close any open option dropdown when clicking outside it
    // uses mousedown to avoid label-forwarded click events retriggering close
    document.addEventListener("mousedown", (e) => {
        if (e.target.closest(".productV1ContentOptionsItemDropdown")) return;
        closeOptionDropdowns();
    });

    function closeOptionDropdowns() {
        section.querySelectorAll(".productV1ContentOptionsItemDropdownPopover.active").forEach(p => p.classList.remove("active"));
        section.querySelectorAll(".productV1ContentOptionsItemDropdownToggle[aria-expanded='true']").forEach(t => t.setAttribute("aria-expanded", "false"));
    }

    // mobile: pulse swatch scale on tap (avoids stuck :hover scale on iOS)
    const tapPulseTimers = new WeakMap();
    section.addEventListener("click", function (e) {
        const trigger = e.target.closest(".productV1ContentOptionsItemValuesLabel, .pdpUpsellVariantPickerPopoverSwatch");
        if (!trigger) return;
        const swatch = trigger.querySelector(".productV1ContentOptionsItemValuesLabelSwatch, .pdpUpsellVariantPickerPopoverSwatchPreview");
        if (!swatch) return;
        clearTimeout(tapPulseTimers.get(swatch));
        swatch.classList.add("tapPulse");
        tapPulseTimers.set(swatch, setTimeout(() => swatch.classList.remove("tapPulse"), 150));
    });

    /**************
     Product Description
     ***************/

    const description = section.querySelector(".productV1ContentDescription");
    if (description) {
        const descriptionBody = description.querySelector(".productV1ContentDescriptionBody");

        // only show the toggle when the text overflows its 4-line clamp (width-aware)
        function syncDescriptionToggle() {
            if (!descriptionBody) return;
            // Expanded, nothing is clipped by definition — recomputing here would drop
            // hasToggle and take SHOW LESS with it.
            if (description.classList.contains("active")) return;
            // Ask whether the copy is ACTUALLY truncated rather than comparing against
            // lineHeight * 4: paragraph margins add height without adding lines, so a height
            // heuristic reports "overflowing" for copy that fits and shows a SHOW MORE that
            // expands to nothing.
            description.classList.toggle("hasToggle", descriptionBody.scrollHeight > descriptionBody.clientHeight + 1);
        }

        syncDescriptionToggle();
        window.addEventListener("resize", syncDescriptionToggle);
        document.fonts?.ready.then(syncDescriptionToggle);

        // expand / collapse the description
        description.querySelector(".productV1ContentDescriptionMoreBtn")?.addEventListener("click", function () {
            description.classList.add("active");
            this.setAttribute("aria-expanded", "true");
        });
        description.querySelector(".productV1ContentDescriptionLessBtn")?.addEventListener("click", function () {
            description.classList.remove("active");
            this.setAttribute("aria-expanded", "false");
        });
    }

    /**************
     FAQ
     ***************/

    // toggle the faq list open/closed; animate height to the inner list's measured size
    section.querySelector(".productV1FAQToggle")?.addEventListener("click", function () {
        const faq = section.querySelector(".productV1FAQ");
        const wrapper = section.querySelector(".productV1FAQListWrapper");
        const list = section.querySelector(".productV1FAQList");
        const isOpen = faq.classList.toggle("active");
        this.setAttribute("aria-expanded", isOpen ? "true" : "false");
        wrapper.style.height = isOpen ? `${list.offsetHeight}px` : "0px";
    });

    /**************
     Pack Pickers
     ***************/
    // delegated handler: clicking a card's trigger toggles that card's popover (the PDP card has
    // no trigger, so it never matches)
    section.addEventListener("click", (e) => {
        const trigger = e.target.closest(".pdpUpsellItemTrigger");
        if (!trigger) return;
        const popover = trigger.closest(".pdpUpsellItem").querySelector(".pdpUpsellVariantPickerPopover");
        if (!popover) return;
        const isOpen = popover.classList.contains("active");
        closeAllPickers();
        if (!isOpen) {
            openPopover(trigger.closest(".pdpUpsellItem"), popover);
            trigger.setAttribute("aria-expanded", "true");
        }
    });

    // delegated: clicking a swatch marks it active and applies its variant to the picker
    section.addEventListener("click", (e) => {
        const swatchBtn = e.target.closest(".pdpUpsellVariantPickerPopoverSwatch");
        if (!swatchBtn) return;

        // sold-out color variants can't be selected
        if (swatchBtn.classList.contains("soldOut")) return;

        const picker = swatchBtn.closest(".pdpUpsellVariantPicker");

        // the PDP product's card drives the main color selector; updateProduct then syncs the
        // card image, active swatch and price back from the real active variant
        if (picker.dataset.pdp === "true") {
            selectPdpColor(swatchBtn.dataset.value);
            return;
        }

        picker.querySelectorAll(".pdpUpsellVariantPickerPopoverSwatch").forEach(s => s.classList.remove("active"));
        swatchBtn.classList.add("active");
        selectSwatch(picker, swatchBtn, true);
    });

    // click anywhere outside the cards closes any open popover
    document.addEventListener("mousedown", (e) => {
        if (e.target.closest(".pdpUpsellItem")) return;
        closeAllPickers();
    });

    // parse and cache a picker's per-variant data (price, compare, button colors), keyed by variant id
    function pickerData(picker) {
        if (!picker._data) {
            try {
                picker._data = JSON.parse(picker.querySelector(".pdpUpsellVariantPickerJSON").textContent);
            } catch (err) {
                console.error("PDP upsell picker JSON failed to parse:", err);
                picker._data = {};
            }
        }
        return picker._data;
    }

    // apply a swatch choice to its picker: button label/colors and the picker's selected variant
    function selectSwatch(picker, swatchBtn, rerender) {
        const data = pickerData(picker)[swatchBtn.dataset.variant] || {};
        picker.dataset.variant = swatchBtn.dataset.variant;
        picker.dataset.price = data.price;
        picker.dataset.compare = data.compare;
        // swap the card image to the chosen variant
        const cardImg = picker.closest(".pdpUpsellItem")?.querySelector(".pdpUpsellItemImage");
        if (cardImg && data.img) {
            cardImg.src = data.img;
            cardImg.srcset = data.srcset || "";
            cardImg.alt = data.alt || "";
        }
        if (rerender) renderPrice();
    }

    // initialize each picker from its first swatch (colors, label, selected variant)
    // PDP-product pickers are driven by the active variant instead, so skip them here
    section.querySelectorAll(".pdpUpsellVariantPicker").forEach(picker => {
        if (picker.dataset.pdp === "true") return;
        // prefer the first in-stock swatch; fall back to the first swatch if every color is sold out
        const firstSwatch = picker.querySelector(".pdpUpsellVariantPickerPopoverSwatch:not(.soldOut)") || picker.querySelector(".pdpUpsellVariantPickerPopoverSwatch");
        if (!firstSwatch) return;
        firstSwatch.classList.add("active");
        selectSwatch(picker, firstSwatch, false);
    });

    // seed the PDP-product pickers from the current active variant before the first price
    syncPdpPickers();

    // check the main color radio for a value, then fire change so updateProduct recomputes
    // the active variant (and syncs the PDP card image, swatch and price back)
    function selectPdpColor(colorValue) {
        const input = section.querySelector(`.productV1ContentOptionsItemColor input[value="${CSS.escape(colorValue)}"]`);
        if (!input || input.checked) return;
        input.checked = true;
        input.dispatchEvent(new Event("change", {bubbles: true}));
    }

    // color carried across product tabs: adopt the color when this product has
    // a matching option value, otherwise keep the current selection
    document.addEventListener("ohsnap:select-color", (event) => {
        const color = event.detail && event.detail.color;
        if (color) selectPdpColor(color);
    });

    // takeover chapter CTAs dispatch the clicked color's variant id; if it's
    // one of this product's variants, reveal this product's tab group and
    // select that variant's color
    document.addEventListener("ohsnap:select-variant", (event) => {
        const variantId = String((event.detail && event.detail.variantId) || "");
        if (!variantId) return;
        const variant = product.variants.find((v) => String(v.id) === variantId);
        if (!variant) return;
        const myTabs = section.querySelector("[data-product-tabs]");
        if (myTabs && myTabs._ctReveal) myTabs._ctReveal();
        const colorIdx = product.options.findIndex(
            (o) => String(o.name || o).toLowerCase() === "color"
        );
        if (colorIdx === -1) return;
        selectPdpColor(variant.options[colorIdx]);
    });

    // sync every PDP-product card picker to the active variant: swap its image, mark the
    // matching color swatch, and align its dataset so pricing/ATC use the real variant
    function syncPdpPickers() {
        const colorInput = section.querySelector(".productV1ContentOptionsItemColor input:checked");
        const colorValue = colorInput ? colorInput.value : null;
        section.querySelectorAll('.pdpUpsellVariantPicker[data-pdp="true"]').forEach(picker => {
            picker.dataset.variant = activeVariant.id;
            picker.dataset.price = activeVariant.price;
            picker.dataset.compare = activeVariant.compare_at_price || 0;

            picker.querySelectorAll(".pdpUpsellVariantPickerPopoverSwatch").forEach(swatch => {
                swatch.classList.toggle("active", swatch.dataset.value === colorValue);
            });

            const img = picker.closest(".pdpUpsellItem").querySelector(".pdpUpsellItemImage");
            if (img && activeVariant.featured_image) {
                img.src = `${activeVariant.featured_image.src}&width=240`;
                img.removeAttribute("srcset");
                img.alt = activeVariant.featured_image.alt || "";
            }
        });
    }

    // price the initially selected card now that the pickers are populated
    renderPrice();

    // position and open a picker's popover, pointing its arrow at the card
    function openPopover(card, popover) {
        popover.classList.add("active");
        const row = card.parentElement;
        const rowRect = row.getBoundingClientRect();
        const popoverRect = popover.getBoundingClientRect();
        const cardRect = card.getBoundingClientRect();
        const isMobile = window.matchMedia("(max-width: 800px)").matches;
        if (isMobile) {
            // mobile: item is positioned, so the CSS default handles vertical placement
            popover.style.bottom = "";
            // mobile: popover is centered in the row, so aim the arrow at the card
            popover.style.left = "";
            const cardCenter = cardRect.left + (cardRect.width / 2);
            const arrowX = cardCenter - popoverRect.left - 17.5;
            popover.style.setProperty("--arrow-x", `${arrowX}px`);
        } else {
            // desktop: item is static so the popover anchors to the whole multi-row body; pin its bottom to the clicked card's top so it sits above that row
            popover.style.bottom = `${rowRect.bottom - cardRect.top + 4}px`;
            // desktop: clamp the popover inside the row, arrow 40% from the card's left
            const arrowTarget = cardRect.left + (cardRect.width * 0.4);
            let left = arrowTarget - rowRect.left - (popoverRect.width / 2);
            const maxLeft = rowRect.width - popoverRect.width;
            left = Math.max(0, Math.min(left, maxLeft));
            popover.style.left = `${left}px`;
            const arrowX = arrowTarget - (rowRect.left + left) - 17.5;
            popover.style.setProperty("--arrow-x", `${arrowX}px`);
        }
    }

    // close every open card popover (and reset its trigger's aria-expanded)
    function closeAllPickers() {
        section.querySelectorAll(".pdpUpsellItemTrigger[aria-expanded='true']").forEach(t => {
            t.setAttribute("aria-expanded", "false");
            t.closest(".pdpUpsellItem").querySelector(".pdpUpsellVariantPickerPopover").classList.remove("active");
        });
    }

    /************
     Badges
     ************/

    // toggle a badge's info popover; clicking another badge or outside closes any open one
    section.addEventListener("click", (e) => {
        const infoBtn = e.target.closest(".productV1ContentInfoBadgesItemInfo");
        if (!infoBtn) return;
        const badge = infoBtn.closest(".productV1ContentInfoBadgesItem");
        const popover = badge.querySelector(".productV1ContentInfoBadgesItemPopover");
        const willOpen = !popover.classList.contains("active");
        // close all open badge popovers before toggling this one
        section.querySelectorAll(".productV1ContentInfoBadgesItemPopover.active").forEach(p => {
            p.classList.remove("active");
            p.closest(".productV1ContentInfoBadgesItem").querySelector(".productV1ContentInfoBadgesItemInfo").setAttribute("aria-expanded", "false");
        });
        if (willOpen) {
            popover.classList.add("active");
            infoBtn.setAttribute("aria-expanded", "true");
        }
    });

    // close badge popover when clicking outside
    document.addEventListener("mousedown", (e) => {
        if (e.target.closest(".productV1ContentInfoBadgesItem")) return;
        section.querySelectorAll(".productV1ContentInfoBadgesItemPopover.active").forEach(p => {
            p.classList.remove("active");
            p.closest(".productV1ContentInfoBadgesItem").querySelector(".productV1ContentInfoBadgesItemInfo").setAttribute("aria-expanded", "false");
        });
    });

    /************
     Product tabs (page templates)
     Each tabbed product section carries its label/icon/group size as data
     attributes; the first instance to run builds the identical segmented
     control into every instance (self marked active) and wires clicks to
     switch which product's group of sections is visible. Group = the
     instance's section wrapper plus the next (tab_sections - 1) sections.
     Without JS, every group stays visible (stacked), same as before.
     ************/
    const tabsHost = section.querySelector("[data-product-tabs]");
    if (tabsHost && !tabsHost.dataset.built) {
        const hosts = [...document.querySelectorAll("[data-product-tabs]")];
        if (hosts.length > 1) {
            const groups = hosts.map((host) => {
                const wrap = host.closest(".shopify-section");
                const count = parseInt(host.dataset.tabSections, 10) || 2;
                const members = [wrap];
                let el = wrap.nextElementSibling;
                while (el && members.length < count) {
                    if (el.classList.contains("shopify-section")) members.push(el);
                    el = el.nextElementSibling;
                }
                return members;
            });

            const show = (idx) => {
                groups.forEach((members, g) => {
                    members.forEach((m) => {
                        m.style.display = g === idx ? "" : "none";
                    });
                });
            };

            hosts.forEach((host, hostIdx) => {
                if (host.dataset.built) return;
                host.dataset.built = "true";
                // lets a variant-select event reveal this product's group
                host._ctReveal = () => show(hostIdx);
                const track = document.createElement("div");
                track.className = "productV1TabsTrack";
                hosts.forEach((h, i) => {
                    const tab = document.createElement("button");
                    tab.type = "button";
                    tab.className = "productV1TabsTab" + (i === hostIdx ? " active" : "");
                    tab.setAttribute("aria-pressed", i === hostIdx ? "true" : "false");
                    if (h.dataset.tabIconUrl) {
                        const icon = document.createElement("span");
                        icon.className = "productV1TabsIcon";
                        icon.style.webkitMaskImage = `url("${h.dataset.tabIconUrl}")`;
                        icon.style.maskImage = `url("${h.dataset.tabIconUrl}")`;
                        tab.appendChild(icon);
                    }
                    const label = document.createElement("span");
                    label.className = "productV1TabsLabel";
                    label.textContent = h.dataset.tabLabel;
                    tab.appendChild(label);
                    if (i !== hostIdx) {
                        tab.addEventListener("click", () => {
                            // the selected color follows the visitor across tabs:
                            // every product section with a matching color option
                            // value adopts it (no match = keeps its own selection)
                            const current = groups[hostIdx][0].querySelector(
                                ".productV1ContentOptionsItemColor input:checked"
                            );
                            show(i);
                            if (current) {
                                document.dispatchEvent(
                                    new CustomEvent("ohsnap:select-color", { detail: { color: current.value } })
                                );
                            }
                        });
                    }
                    track.appendChild(tab);
                });
                host.appendChild(track);
            });

            show(0);
        }
    }

    /************
     Variant tooltip tap-flash (touch devices)
     Hover reveals the color-name tooltip on desktop; on touch there is no
     hover, so flash it for half a second when a swatch is selected.
     ************/
    if (window.matchMedia("(hover: none)").matches) {
        let tooltipFlashTimer = null;
        section.addEventListener("change", (event) => {
            const label = event.target.closest(".productV1ContentOptionsItemValuesLabel");
            if (!label) return;
            section.querySelectorAll(".productV1ContentOptionsItemValuesLabel.showTooltip").forEach((el) => {
                el.classList.remove("showTooltip");
            });
            clearTimeout(tooltipFlashTimer);
            label.classList.add("showTooltip");
            tooltipFlashTimer = setTimeout(() => label.classList.remove("showTooltip"), 1000);
        });
    }

    /************
     Variant themes (variant_theme blocks, styles in pdp-variant-theme.css)
     Selecting a themed color fades the section into that color's look; any other color fades
     it back. Runs on the swatch change itself rather than after updateProduct()'s fetch, so the
     fade starts on the click. Liquid already rendered the theme of the variant the page opened on.
     ************/
    const themeLayers = [...section.querySelectorAll(".productV1ThemeLayer")];
    if (themeLayers.length) {
        const themeLogosWrap = section.querySelector(".productV1ThemeLogos");
        const themeLogos = [...section.querySelectorAll(".productV1ThemeLogo")];
        const normalizeColor = (value) => String(value || "").trim().toLowerCase();
        const patternLoads = new Map();
        let themeToken = 0;

        // attach a layer's pattern once the image has loaded and fade it in (.patternReady, CSS).
        // Resolves on load, failure or after 1.5s, whichever is first, so a slow tile never holds
        // up the theme switch: the dark base fades in and the tile follows when it arrives.
        function loadPattern(layer) {
            const src = layer.dataset.pattern;
            if (!src) return Promise.resolve();
            if (!patternLoads.has(layer)) {
                patternLoads.set(layer, new Promise((resolve) => {
                    const img = new Image();
                    img.onload = () => {
                        layer.style.setProperty("--theme-pattern", `url("${src}")`);
                        layer.classList.add("patternReady");
                        resolve();
                    };
                    img.onerror = resolve;
                    setTimeout(resolve, 1500);
                    img.src = src;
                }));
            }
            return patternLoads.get(layer);
        }

        // the theme the page opened on: Liquid painted its dark base; its tile waits for the first
        // gallery image, so the product photo (the page's LCP) gets the bandwidth first
        const openingLayer = themeLayers.find((layer) => layer.classList.contains("active"));
        if (openingLayer) {
            const heroImage = section.querySelector('.productV1VisualSwiperSlideImage[fetchpriority="high"]');
            const loadOpening = () => loadPattern(openingLayer);
            if (!heroImage || heroImage.complete) {
                loadOpening();
            } else {
                heroImage.addEventListener("load", loadOpening, { once: true });
                heroImage.addEventListener("error", loadOpening, { once: true });
                setTimeout(loadOpening, 4000);
            }
        }

        function applyVariantTheme(colorValue) {
            const key = normalizeColor(colorValue);
            const nextLayer = themeLayers.find((layer) => layer.dataset.themeColor === key) || null;
            const token = ++themeToken;

            section.classList.add("productV1ThemeAnimate");
            section.classList.toggle("productV1Themed", !!nextLayer);
            // the accent stays on the last theme's value when leaving, so it fades out in that color
            if (nextLayer?.dataset.themeAccent) {
                section.style.setProperty("--theme-accent", nextLayer.dataset.themeAccent);
            }

            // theme to theme (e.g. APX to Snow): the outgoing pattern stays fully opaque underneath
            // (.leaving) while the incoming one fades in over it, then drops away once covered.
            // Crossfading both at once would let the section's light background show through mid-fade.
            const prevLayer = themeLayers.find((layer) => layer.classList.contains("active")) || null;
            const handOff = !!(nextLayer && prevLayer && prevLayer !== nextLayer);
            themeLayers.forEach((layer) => {
                if (layer === nextLayer) {
                    layer.classList.remove("leaving");
                } else if (layer === prevLayer && handOff) {
                    layer.classList.add("leaving");
                    layer.classList.remove("active");
                } else {
                    layer.classList.remove("active", "leaving");
                }
            });
            if (nextLayer) {
                loadPattern(nextLayer).then(() => {
                    if (token !== themeToken) return;
                    nextLayer.classList.add("active");
                    if (!handOff) return;
                    let released = false;
                    const release = () => {
                        if (released) return;
                        released = true;
                        nextLayer.removeEventListener("transitionend", onEnd);
                        if (token === themeToken) prevLayer.classList.remove("leaving");
                    };
                    const onEnd = (event) => {
                        if (event.target === nextLayer && event.propertyName === "opacity") release();
                    };
                    nextLayer.addEventListener("transitionend", onEnd);
                    setTimeout(release, 1500);
                });
            }

            if (themeLogosWrap) {
                const currentLogo = themeLogos.find((logo) => logo.classList.contains("active")) || null;
                const nextLogo = nextLayer ? themeLogos.find((logo) => logo.dataset.themeColor === key) || null : null;
                // two camo colors sharing one logo: keep it up instead of crossfading it into itself
                const keepCurrent = currentLogo && nextLogo && currentLogo.src === nextLogo.src;
                if (!keepCurrent) {
                    themeLogos.forEach((logo) => logo.classList.toggle("active", logo === nextLogo));
                }
                themeLogosWrap.classList.toggle("active", !!nextLogo);
            }
        }

        section.addEventListener("change", (event) => {
            const input = event.target.closest(".productV1ContentOptionsItemColor input");
            if (input && input.checked) applyVariantTheme(input.value);
        });

        // warm a theme's pattern when its swatch is approached (hover, keyboard focus, or the touch
        // before the tap), so a first click usually fades straight in. Not on page load: these are
        // the main grip PDPs, and most shoppers never go near a camo color.
        const warmFromSwatch = (event) => {
            const input = event.target.closest?.(".productV1ContentOptionsItemValuesLabel")?.querySelector("input");
            if (!input) return;
            const layer = themeLayers.find((l) => l.dataset.themeColor === normalizeColor(input.value));
            if (layer) loadPattern(layer);
        };
        ["pointerover", "focusin", "touchstart"].forEach((type) => section.addEventListener(type, warmFromSwatch, { passive: true }));
    }

    /************
     Themed reviews marquee (theme_review blocks, shown only while a variant theme is active)
     Scrolls continuously at data-speed px/s. Runs only while it is on screen and themed; Pause
     stops it, dragging moves it by hand, and the offset wraps by one list width so the loop is
     seamless. Reduced motion starts paused (the shopper can still press Play or drag).
     ************/
    let themeReviews = section.querySelector(".productV1ThemeReviews");
    // shared reviews: a section with no Themed Review blocks of its own renders an empty slot, filled
    // here with a clean copy of another product section's strip (that section's script may already
    // have cloned lists into it and moved it, so strip those back to the first list at rest)
    const themeReviewsSlot = section.querySelector(".productV1ThemeReviewsSlot");
    if (!themeReviews && themeReviewsSlot) {
        const source = document.querySelector(".productV1ThemeReviews");
        if (source) {
            themeReviews = source.cloneNode(true);
            const copyTrack = themeReviews.querySelector(".productV1ThemeReviewsTrack");
            [...copyTrack.children].slice(1).forEach((list) => list.remove());
            copyTrack.style.transform = "";
            themeReviews.querySelector(".productV1ThemeReviewsViewport").classList.remove("dragging");
            themeReviews.querySelectorAll("[data-shopify-editor-block]").forEach((el) => el.removeAttribute("data-shopify-editor-block"));
            themeReviewsSlot.replaceWith(themeReviews);
        }
    }
    if (themeReviews) {
        const viewport = themeReviews.querySelector(".productV1ThemeReviewsViewport");
        const track = themeReviews.querySelector(".productV1ThemeReviewsTrack");
        const firstList = track.querySelector(".productV1ThemeReviewsList");
        const pauseBtn = themeReviews.querySelector(".productV1ThemeReviewsPause");
        const pauseLabel = themeReviews.querySelector(".productV1ThemeReviewsPauseLabel");
        const speed = parseFloat(themeReviews.dataset.speed) || 60;
        let paused = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        let inView = false;
        let dragging = false;
        let offset = 0;
        let listWidth = 0;
        let lastTime = 0;
        let rafId = 0;

        // clone the list until the track covers the viewport plus one list, so the wrap never shows a gap
        function measure() {
            listWidth = firstList.getBoundingClientRect().width;
            if (!listWidth) return false;
            let copies = track.children.length;
            while (copies * listWidth < viewport.clientWidth + listWidth && copies < 12) {
                const clone = firstList.cloneNode(true);
                clone.setAttribute("aria-hidden", "true");
                clone.querySelectorAll("[data-shopify-editor-block]").forEach((el) => el.removeAttribute("data-shopify-editor-block"));
                track.appendChild(clone);
                copies++;
            }
            return true;
        }

        function render() {
            if (listWidth) offset = ((offset % listWidth) + listWidth) % listWidth;
            track.style.transform = `translate3d(${-offset}px, 0, 0)`;
        }

        const shouldRun = () => inView && !paused && !dragging && section.classList.contains("productV1Themed");

        function frame(time) {
            rafId = 0;
            if (!shouldRun()) return;
            if (!listWidth && !measure()) {
                rafId = requestAnimationFrame(frame);
                return;
            }
            const dt = lastTime ? Math.min(time - lastTime, 64) / 1000 : 0;
            lastTime = time;
            offset += speed * dt;
            render();
            rafId = requestAnimationFrame(frame);
        }

        function kick() {
            if (rafId || !shouldRun()) return;
            lastTime = 0;
            rafId = requestAnimationFrame(frame);
        }

        function setPaused(value) {
            paused = value;
            pauseBtn.setAttribute("aria-pressed", paused ? "true" : "false");
            pauseLabel.textContent = paused ? "Play" : "Pause";
            kick();
        }
        setPaused(paused);
        pauseBtn.addEventListener("click", () => setPaused(!paused));

        // drag by hand; vertical page scrolling still works on touch (touch-action: pan-y)
        let dragStartX = 0;
        let dragStartOffset = 0;
        viewport.addEventListener("pointerdown", (event) => {
            if (event.button !== 0) return;
            if (!listWidth) measure();
            dragging = true;
            dragStartX = event.clientX;
            dragStartOffset = offset;
            viewport.classList.add("dragging");
            viewport.setPointerCapture(event.pointerId);
        });
        viewport.addEventListener("pointermove", (event) => {
            if (!dragging) return;
            offset = dragStartOffset - (event.clientX - dragStartX);
            render();
        });
        const endDrag = () => {
            if (!dragging) return;
            dragging = false;
            viewport.classList.remove("dragging");
            kick();
        };
        viewport.addEventListener("pointerup", endDrag);
        viewport.addEventListener("pointercancel", endDrag);

        new IntersectionObserver((entries) => {
            inView = entries[0].isIntersecting;
            kick();
        }, { rootMargin: "100px" }).observe(themeReviews);

        // start when a theme turns on (the strip was display:none, so re-measure then)
        new MutationObserver(() => {
            if (!section.classList.contains("productV1Themed")) return;
            listWidth = 0;
            kick();
        }).observe(section, { attributes: true, attributeFilter: ["class"] });

        window.addEventListener("resize", () => {
            listWidth = 0;
            measure();
            render();
        });
    }
}());